// The overlay canvas (G5): artboard names, bleed and margin guides, then the active tool's feedback.
// The document itself is drawn by the tile renderer (tiles.ts).
import type { Doc } from '../model/types';
import type { View } from '../state';
import { cssVar } from '../../app/theme';

/** What a tool wants drawn on the overlay this frame (selection boxes, handles, rubber bands …). */
export type Interaction = { draw: (g: CanvasRenderingContext2D, v: View) => void };

export function drawOverlay(
  g: CanvasRenderingContext2D,
  doc: Doc,
  v: View,
  dpr: number,
  it: { draw: (g: CanvasRenderingContext2D, v: View) => void } | null,
  show: { grid: boolean; gridSpacing: number; guides: boolean } = { grid: false, gridSpacing: 18, guides: true },
) {
  g.setTransform(1, 0, 0, 1, 0, 0);
  g.clearRect(0, 0, g.canvas.width, g.canvas.height);
  g.setTransform(dpr, 0, 0, dpr, 0, 0);
  const W = g.canvas.width / dpr,
    H = g.canvas.height / dpr;
  if (show.grid) {
    // Document grid (every 5th line darker); skipped when lines would be closer than 6 px.
    let step = show.gridSpacing;
    while (step * v.zoom < 6) step *= 5;
    g.lineWidth = 1;
    const x0 = Math.floor(-v.panX / v.zoom / step) * step,
      y0 = Math.floor(-v.panY / v.zoom / step) * step;
    for (let x = x0; x * v.zoom + v.panX < W; x += step) {
      const sx = Math.round(x * v.zoom + v.panX) + 0.5;
      g.strokeStyle = Math.round(x / step) % 5 === 0 ? cssVar('--grid-strong') : cssVar('--grid-weak');
      g.beginPath();
      g.moveTo(sx, 0);
      g.lineTo(sx, H);
      g.stroke();
    }
    for (let y = y0; y * v.zoom + v.panY < H; y += step) {
      const sy = Math.round(y * v.zoom + v.panY) + 0.5;
      g.strokeStyle = Math.round(y / step) % 5 === 0 ? cssVar('--grid-strong') : cssVar('--grid-weak');
      g.beginPath();
      g.moveTo(0, sy);
      g.lineTo(W, sy);
      g.stroke();
    }
  }
  if (show.guides) {
    g.strokeStyle = cssVar('--guide');
    g.lineWidth = 1;
    for (const gd of doc.guides) {
      g.beginPath();
      if (gd.axis === 'x') {
        const sx = Math.round(gd.pos * v.zoom + v.panX) + 0.5;
        g.moveTo(sx, 0);
        g.lineTo(sx, H);
      } else {
        const sy = Math.round(gd.pos * v.zoom + v.panY) + 0.5;
        g.moveTo(0, sy);
        g.lineTo(W, sy);
      }
      g.stroke();
    }
  }
  g.lineWidth = 1;
  g.font = '11px system-ui, sans-serif';
  for (const a of doc.artboards) {
    const sx = a.x * v.zoom + v.panX,
      sy = a.y * v.zoom + v.panY,
      sw = a.w * v.zoom,
      sh = a.h * v.zoom;
    g.fillStyle = cssVar('--artboard-label');
    g.fillText(a.name, sx, sy - 6);
    const box = (
      b: { top: number; right: number; bottom: number; left: number },
      sign: number,
      color: string,
    ) => {
      if (!b.top && !b.right && !b.bottom && !b.left) return;
      g.strokeStyle = color;
      g.setLineDash([4, 3]);
      g.strokeRect(
        Math.round(sx - sign * b.left * v.zoom) + 0.5,
        Math.round(sy - sign * b.top * v.zoom) + 0.5,
        Math.round(sw + sign * (b.left + b.right) * v.zoom),
        Math.round(sh + sign * (b.top + b.bottom) * v.zoom),
      );
      g.setLineDash([]);
    };
    box(a.bleed, 1, cssVar('--bleed-line')); // bleed: outside the trim
    box(a.margins, -1, cssVar('--margin-line')); // margins: inside
  }
  it?.draw(g, v);
}

export const rgbCss = (c: [number, number, number], alpha = 1) =>
  `rgba(${Math.round(c[0] * 255)},${Math.round(c[1] * 255)},${Math.round(c[2] * 255)},${alpha})`;
